import { useTranslation } from "react-i18next";
import { Calendar, Clock, Send, Ticket, Users } from "lucide-react";
import { SectionCard } from "@/components/common";
import { formatFullDate } from "@/components/tickets/config";
import { formatMoney } from "@/lib/money";
import { BookingCancellationSource } from "./BookingCancellationSource";
import { BookingPartnerStatusInsight } from "./BookingPartnerStatusInsight";
import { getPartnerVisitDateTimeLabels } from "./partnerBookingCardPresentation";
import { bookingPartyCount } from "./bookingPartyCount";
import {
  getUpfrontPaidCents,
  lineItemStrikethroughTotalCents,
  sumBookingDiscountBreakdown,
} from "./sellerBookingRules";
import {
  BookingPriceBreakdown,
  LineItemDiscountBadges,
} from "./BookingPriceBreakdown";
import type { BookingListRow } from "./bookingViewModel";
import type { Booking } from "@/types";
import type { ReactNode } from "react";

interface BookingPartnerSummaryCardProps {
  row: BookingListRow;
  booking: Booking;
  footer?: ReactNode;
  className?: string;
}

export function BookingPartnerSummaryCard({
  row,
  booking,
  footer,
  className,
}: BookingPartnerSummaryCardProps) {
  const { t } = useTranslation("bookings");
  const { dateLabel, timeLabel } = getPartnerVisitDateTimeLabels(row);
  const partyCount = bookingPartyCount(row);
  const partyLabel = t("guest", { count: partyCount });
  const upfrontPaidCents = getUpfrontPaidCents(row);
  const priceBreakdown = sumBookingDiscountBreakdown(row.lineItems);
  const cancellationSource =
    row.status === "CANCELED" ? row.cancellationSource : null;
  const bookingTypeLabel =
    row.type === "receipt_based"
      ? t("detail.bookingTypes.restaurant")
      : t("detail.bookingTypes.service");
  const showAmountPaid = upfrontPaidCents != null;
  const pointsDiscountCents = row.pointsDiscountCents ?? null;
  const showDiscountBreakdown =
    priceBreakdown != null &&
    (priceBreakdown.hasAnyDiscount ||
      (pointsDiscountCents != null && pointsDiscountCents > 0));

  return (
    <SectionCard
      title={t("detail.bookingSummary")}
      icon={Ticket}
      className={className}
      contentClassName="space-y-4"
    >
      {row.status === "ABANDONED" ? (
        <BookingPartnerStatusInsight status={row.status} />
      ) : null}

      {cancellationSource ? (
        <BookingCancellationSource
          source={cancellationSource}
          variant="detail"
        />
      ) : null}

      <div className="rounded-lg border border-primary-200/70 bg-primary-50/50 px-4 py-3.5 dark:bg-primary-50/30">
        <p className="text-xs font-medium text-primary-700">
          {t("detail.requestedVisit")}
        </p>
        <p className="mt-1.5 flex items-start gap-2 text-lg font-semibold capitalize tracking-tight text-surface-900 sm:text-xl">
          <Calendar
            className="mt-1 h-4 w-4 shrink-0 text-primary-600"
            aria-hidden
          />
          <span className="min-w-0">{dateLabel ?? "—"}</span>
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-primary-200/60 pt-3 text-sm font-medium tabular-nums text-primary-900">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-4 w-4 shrink-0 text-primary-600" aria-hidden />
            {timeLabel ?? "—"}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Users className="h-4 w-4 shrink-0 text-primary-600" aria-hidden />
            {partyCount > 0 ? partyLabel : "—"}
          </span>
        </div>
      </div>

      {row.lineItems.length > 0 ? (
        <div>
          <p className="text-xs font-medium text-surface-500">
            {t("detail.services")}
          </p>
          <ul className="mt-2 divide-y divide-surface-200/70 overflow-hidden rounded-lg border border-surface-200">
            {row.lineItems.map((item) => {
              const strikeTotal =
                item.totalPriceCents != null
                  ? lineItemStrikethroughTotalCents({
                      listTotalPriceCents: item.listTotalPriceCents,
                      sellerDiscountCents: item.sellerDiscountCents,
                      originalTotalPriceCents: item.originalTotalPriceCents,
                      totalPriceCents: item.totalPriceCents,
                    })
                  : null;
              return (
                <li
                  key={item.id}
                  className="flex items-start justify-between gap-3 bg-surface-0 px-3 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-surface-900">
                      {item.name}
                    </p>
                    {item.unitPriceCents != null ? (
                      <p className="mt-0.5 text-xs text-surface-500">
                        {t("adminDetail.lineItems.qtyUnit", {
                          qty: item.quantity,
                          unit: formatMoney(item.unitPriceCents),
                        })}
                      </p>
                    ) : null}
                    <LineItemDiscountBadges
                      sellerDiscountCents={item.sellerDiscountCents}
                      flitripDiscountCents={item.flitripDiscountCents}
                    />
                  </div>
                  {item.totalPriceCents != null ? (
                    <div className="shrink-0 text-right">
                      {strikeTotal != null ? (
                        <p className="text-xs tabular-nums text-surface-400 line-through">
                          {formatMoney(strikeTotal)}
                        </p>
                      ) : null}
                      <p className="text-sm font-semibold tabular-nums text-surface-900">
                        {formatMoney(item.totalPriceCents)}
                      </p>
                    </div>
                  ) : (
                    <span className="shrink-0 tabular-nums text-sm font-semibold text-surface-500">
                      ×{item.quantity}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {showDiscountBreakdown ||
      showAmountPaid ||
      booking.customer_notes?.trim() ? (
        <div className="space-y-4">
          {showDiscountBreakdown && priceBreakdown ? (
            <BookingPriceBreakdown
              breakdown={priceBreakdown}
              pointsDiscountCents={pointsDiscountCents}
              customerPaidCents={showAmountPaid ? upfrontPaidCents : null}
            />
          ) : showAmountPaid ? (
            <div className="flex items-baseline justify-between gap-3 rounded-lg border border-surface-200 bg-surface-50 px-4 py-3">
              <span className="text-sm font-medium text-surface-500">
                {t("detail.amountPaid")}
              </span>
              <span className="text-xl font-semibold tracking-tight tabular-nums text-surface-900 sm:text-2xl">
                {formatMoney(upfrontPaidCents)}
              </span>
            </div>
          ) : null}

          {booking.customer_notes?.trim() ? (
            <div>
              <p className="text-xs font-medium text-surface-500">
                {t("detail.customerNotes")}
              </p>
              <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-surface-700">
                {booking.customer_notes}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-3 border-t border-surface-200/70 pt-4 sm:grid-cols-2">
        <div className="flex items-start gap-2">
          <Send
            className="mt-0.5 h-4 w-4 shrink-0 text-surface-400"
            aria-hidden
          />
          <div className="min-w-0">
            <p className="text-xs font-medium text-surface-500">
              {t("detail.bookingType")}
            </p>
            <p className="mt-0.5 text-sm text-surface-700">{bookingTypeLabel}</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Calendar
            className="mt-0.5 h-4 w-4 shrink-0 text-surface-400"
            aria-hidden
          />
          <div className="min-w-0">
            <p className="text-xs font-medium text-surface-500">
              {t("detail.createdAt")}
            </p>
            <p className="mt-0.5 text-sm tabular-nums text-surface-700">
              {formatFullDate(row.createdAt)}
            </p>
          </div>
        </div>
      </div>

      {footer}
    </SectionCard>
  );
}
